/**
 * StatusSummaryCard Component
 *
 * Displays a grouped summary of agreement status information in a compact card format.
 * Shows: Agreement status, period status, data ingestion date, and flags (historical, closed).
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatDateDisplay } from '@/utils/dateUtils';
import { formatCurrency } from '@erp/shared-utils';
import {
  LockIcon,
  CalendarIcon,
  DatabaseIcon,
  ArchiveIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import type { RebateAgreementClosureMetadata } from '@/api/rebates/types';

interface StatusSummaryCardProps {
  status: 'draft' | 'finalized';
  startDate: string;
  endDate: string;
  isHistorical?: boolean;
  isClosedOut?: boolean;
  closureMetadata?: RebateAgreementClosureMetadata | null;
  lastDataIngestionDate?: string | null;
}

export function StatusSummaryCard({
  status,
  startDate,
  endDate,
  isHistorical,
  isClosedOut,
  closureMetadata,
  lastDataIngestionDate,
}: StatusSummaryCardProps) {
  // Calculate period status
  const todayString = new Date().toISOString().split('T')[0];
  const isActive = startDate <= todayString && endDate >= todayString;
  const isUpcoming = startDate > todayString;
  const isExpired = endDate < todayString;

  const getPeriodStatus = () => {
    if (isUpcoming) {
      return {
        label: 'Upcoming',
        description: `Starts on ${formatDateDisplay(startDate)}`,
        variant: 'secondary' as const,
        className: 'bg-blue-50 text-blue-700 border-blue-200',
      };
    }
    if (isActive) {
      return {
        label: 'Active',
        description: `Active until ${formatDateDisplay(endDate)}`,
        variant: 'default' as const,
        className: 'bg-green-50 text-green-700 border-green-200',
      };
    }
    return {
      label: 'Expired',
      description: `Expired on ${formatDateDisplay(endDate)}`,
      variant: 'destructive' as const,
      className: 'bg-red-50 text-red-700 border-red-200',
    };
  };

  const periodStatus = getPeriodStatus();

  return (
    <div className="flex items-center gap-4 px-4 py-2 bg-muted/50 rounded-md border border-border w-fit">
      {/* Agreement Status */}
      <div className="flex items-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5">
                <LockIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <Badge
                  variant={status === 'draft' ? 'secondary' : 'default'}
                  className={
                    status === 'draft'
                      ? 'bg-orange-50 text-orange-700 border-orange-200'
                      : 'bg-green-50 text-green-700 border-green-200'
                  }
                >
                  {status === 'draft' ? 'Draft' : 'Finalized'}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                <strong>Agreement Status:</strong>{' '}
                {status === 'draft'
                  ? 'Rules can be edited and modified'
                  : 'Editing rules does not change reported data'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="h-6 w-px bg-border" />

      {/* Period Status */}
      <div className="flex items-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5">
                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <Badge
                  variant={periodStatus.variant}
                  className={periodStatus.className}
                >
                  {periodStatus.label}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                <strong> {periodStatus.description} </strong>
              </p>
              <p className="text-xs mt-1 text-muted-foreground">
                Period: {formatDateDisplay(startDate)} -{' '}
                {formatDateDisplay(endDate)}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Last Data Ingestion Date */}
      {lastDataIngestionDate ? (
        <>
          <div className="h-6 w-px bg-border" />
          <div className="flex items-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5">
                    <DatabaseIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    <Badge
                      variant="outline"
                      className="bg-blue-50 text-blue-700 border-blue-200"
                    >
                      Last Data: {formatDateDisplay(lastDataIngestionDate)}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    <strong>Last Data Ingestion:</strong>{' '}
                    {formatDateDisplay(lastDataIngestionDate)}
                  </p>
                  <p className="text-xs mt-1 text-muted-foreground">
                    The last date of vendor csv ingestion
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </>
      ) : (
        <>
          <div className="h-6 w-px bg-border" />
          <div className="flex items-center">
            <div className="flex items-center gap-1.5 opacity-50">
              <DatabaseIcon className="h-3.5 w-3.5 text-muted-foreground" />
              <Badge
                variant="outline"
                className="bg-gray-50 text-gray-500 border-gray-200"
              >
                No data
              </Badge>
            </div>
          </div>
        </>
      )}

      {/* Flags */}
      {(isHistorical || isClosedOut) && (
        <>
          <div className="h-6 w-px bg-border" />
          <div className="flex items-center gap-1.5">
            {isHistorical && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="bg-gray-50 text-gray-700 border-gray-200"
                    >
                      <ArchiveIcon className="h-3 w-3 mr-1" />
                      Historical
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      <strong>Historical Agreement:</strong> Excluded from new
                      processing
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {isClosedOut && closureMetadata && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="bg-slate-100 text-slate-700 border-slate-200"
                    >
                      <XCircleIcon className="h-3 w-3 mr-1" />
                      Closed
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <div>
                        <strong>Closed:</strong>{' '}
                        {new Date(
                          closureMetadata.closedAt,
                        ).toLocaleDateString()}
                      </div>
                      <div>
                        <strong>By:</strong> {closureMetadata.closedByUserName}
                      </div>
                      <div>
                        <strong>Reason:</strong> {closureMetadata.reason}
                      </div>
                      {closureMetadata.snapshotData.writeOffAmount !== 0 && (
                        <div>
                          <strong>Write-off:</strong>{' '}
                          {formatCurrency(
                            Math.abs(
                              closureMetadata.snapshotData.writeOffAmount,
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </>
      )}
    </div>
  );
}
